import {FC, useRef, useMemo} from 'react'
import {Content} from '@prismicio/client'
import {PrismicRichText, SliceComponentProps} from '@prismicio/react'
import {Container} from 'src/common/Container'
import {PrismicNextImage} from '@prismicio/next'
import {motion, useInView} from 'framer-motion'
import {EVENTS, getButtonsData, track} from 'src/common/helpers'
import {Context} from 'src/common/types'
import cn from 'classnames'
import Link from 'next/link'
import {Icon} from 'src/common/Icon'
import {useLinkResolver} from 'prismicio'

/**
 * Props for `IntegrationsSection`.
 */
export type IntegrationsSectionProps = SliceComponentProps<Content.IntegrationsSectionSlice> & {
  context: Context
}

/**
 * Component for "IntegrationsSection" Slices.
 */
const IntegrationsSection: FC<IntegrationsSectionProps> = ({slice, context, index: componentIndex}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const {linkResolver} = useLinkResolver()

  const {
    primary: {first_row_logos, second_row_logos, third_row_logos, primary_button},
  } = slice

  const logos = useMemo(
    () => [first_row_logos, second_row_logos, third_row_logos],
    [first_row_logos, second_row_logos, third_row_logos],
  )

  const {primaryButtonData} = getButtonsData({
    buttonsData: context.buttonsData,
    primaryButton: primary_button,
  })

  const isInView = useInView(containerRef, {
    once: false,
    amount: 0.3,
  })

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="gap-16 !pb-0 !pt-20"
      backgroundColor="Dark Green"
      containerRef={containerRef}
    >
      <div className="flex w-full flex-col">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-3 px-5 md:gap-6 md:px-10 lg:px-5 xl:px-0">
          <PrismicRichText
            field={slice.primary.tag}
            components={{
              paragraph: ({children}) => <p className="text-button-s text-moss-white/[80%]">{children}</p>,
              strong: ({children}) => <span className="text-green-neon">{children}</span>,
            }}
          />
          <PrismicRichText
            field={slice.primary.heading}
            components={{
              heading1: ({children}) => (
                <h1 className="md:text-tg-h1 text-[32px] font-normal leading-9 tracking-[-.32px] text-moss-white">
                  {children}
                </h1>
              ),
              strong: ({children}) => (
                <>
                  <br className="max-lg:hidden" />
                  <span className="text-moss-white/[60%]">{children}</span>
                </>
              ),
            }}
          />
          <p className="text-tg-body-l w-full max-w-[394px] text-moss-white/[80%] max-md:py-3">
            {slice.primary.description}
          </p>
          <Link
            href={linkResolver(primaryButtonData?.link)}
            className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] bg-moss-white/[15%] pl-[14px] pr-[7px] hover:bg-moss-white/[12%]"
            onClick={() =>
              track({
                event: EVENTS.CLICK,
                mktgId: primaryButtonData?.marketing_id!,
                pageName: context.pageUid,
                location: `component-${componentIndex}-integrations-section`,
                dataType: 'primary-button',
              })
            }
          >
            <span className="text-button-s uppercase text-moss-white">{primaryButtonData?.label}</span>
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-white/[10%]" />
          </Link>
        </div>

        <div className="relative isolate grid w-full grid-cols-[minmax(20px,_1fr)_minmax(auto,1200px)_minmax(20px,_1fr)] grid-rows-[80px,335px,80px] md:grid-rows-[80px,514px,80px] md:max-lg:grid-cols-[minmax(40px,_1fr)_minmax(auto,1200px)_minmax(40px,_1fr)]">
          <Gradient color="0 36 20" gradientStart={20} height={60} location="top" />

          {/* First row */}
          <div className="col-span-1 rounded-br-[4px] border-b border-r border-green-light" />
          <div className="col-span-1 ml-[-1px] rounded-b-[4px] border-x border-b border-green-light" />
          <div className="col-span-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-green-light" />

          {/* Content */}
          <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-green-light" />
          <div className="col-span-1 ml-[-1px] mt-[-1px] flex flex-col items-center justify-center gap-[14px] overflow-hidden rounded-[4px] border border-green-light max-md:overflow-hidden md:gap-6">
            {logos.map((row, rowIndex) => (
              <div key={`row-${rowIndex}`} className="flex gap-[14px] md:gap-6">
                {row.map((logo, colIndex) => {
                  const finalOpacity = getOpacity(rowIndex, colIndex)
                  const isCenter = rowIndex === 1 && colIndex === 3

                  return (
                    <motion.div
                      key={`logo-${rowIndex}-${colIndex}`}
                      className={cn(
                        'relative isolate flex h-[70px] w-[70px] items-center justify-center rounded-[10px] border-[2px] border-moss-white/[20%] md:h-[120px] md:w-[120px]',
                        {
                          'bg-[linear-gradient(137deg,#143B29_5.58%,#0D2C1E_108.53%)]': !isCenter,
                          'bg-[linear-gradient(137deg,#C1FF56_5.58%,#B9FF3F_108.53%)]': isCenter,
                        },
                      )}
                      initial={{opacity: 0, scale: 0.5}}
                      animate={isInView ? {opacity: finalOpacity, scale: 1} : {opacity: 0, scale: 0.5}}
                      transition={{
                        duration: 0.5,
                        delay: getAnimationDelay(rowIndex, colIndex),
                        ease: 'easeOut',
                      }}
                    >
                      <div
                        className={cn(
                          'pointer-events-none absolute left-[-3px] top-[-3px] z-10 h-[74px] w-[74px] rounded-[10px] opacity-20 md:h-[124px] md:w-[124px]',
                          {
                            'bg-[linear-gradient(137deg,#143B29_5.58%,#0D2C1E_108.53%)]': !isCenter,
                            'bg-[linear-gradient(137deg,#C1FF56_5.58%,#B9FF3F_108.53%)]': isCenter,
                          },
                        )}
                      />
                      {logo.logo && (
                        <PrismicNextImage
                          field={logo.logo}
                          className="z-20 h-12 w-12 object-contain md:h-20 md:w-20"
                          alt=""
                        />
                      )}
                    </motion.div>
                  )
                })}
              </div>
            ))}
          </div>
          <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-green-light" />

          {/* Last Row */}
          <div className="col-span-1 mt-[-1px] rounded-tr-[4px] border-r border-t border-green-light" />
          <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-green-light" />
          <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-green-light" />

          <Gradient color="0 36 20" gradientStart={20} height={60} location="bottom" />
        </div>
      </div>
    </Container>
  )
}

const Gradient = ({
  color,
  gradientStart,
  height,
  location,
}: {
  color: string
  gradientStart: number
  height: number
  location: 'top' | 'bottom'
}) => {
  return (
    <div
      className={`absolute w-full ${location === 'top' ? 'top-0' : 'bottom-0 mt-[-1px]'}`}
      style={{
        backgroundImage: `linear-gradient(to ${
          location === 'top' ? 'bottom' : 'top'
        }, rgb(${color}), rgb(${color}) ${gradientStart}%, transparent)`,
        height: `${height}px`,
      }}
    />
  )
}

export default IntegrationsSection

const getAnimationDelay = (rowIndex: number, colIndex: number) => {
  const centerRow = 1
  const centerCol = 3

  const rowDistance = Math.abs(rowIndex - centerRow)
  const colDistance = Math.abs(colIndex - centerCol)

  const distance = rowDistance + colDistance
  return distance * 0.1
}

const getOpacity = (rowIndex: number, colIndex: number) => {
  if (
    (rowIndex === 0 && colIndex >= 1 && colIndex <= 3) ||
    (rowIndex === 1 && colIndex >= 2 && colIndex <= 4) ||
    (rowIndex === 2 && colIndex >= 1 && colIndex <= 3)
  ) {
    return 1
  } else if (rowIndex === 1 && (colIndex === 0 || colIndex === 6)) {
    return 0.2
  } else {
    return 0.4
  }
}
